/**
 * roteiro-dia.tsx — Roteiro imprimível de UM dia da agenda (Fatia 3).
 *
 * O "produto final" da Suany, hoje feito à mão no Word. Puxa as paradas do dia
 * (eventos da agenda) na ordem, agrupadas por destino, cada uma com cliente,
 * ação, pedido, OS, notas fiscais (do fornecedor) e "o que levar" — os dados
 * que nascem na aba Roteiro da OS. Determinístico, imprime via "Salvar como PDF".
 *
 * Roteiro ≠ OS impressa: aqui é a prévia leve do dia; o detalhe (specs/fotos)
 * continua em /ordens-servico/:id/imprimir. Escopo: um dia (viagem multi-dia é
 * evolução). Ordem = a que vier do backend (hora, depois criação); reordenar
 * arrastando fica pra depois.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import type {
  RoteiroDia,
  RoteiroEvento,
  RoteiroViagem,
} from '@/pages/ordens-servico/shared';

const MARROM = '#5b4a2f';
const DIAS = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
];
const LOJAS: Record<string, string> = { BZ: 'Búzios', TR: 'Trancoso' };

const v = (s?: string | null) => (s && s.trim() ? s.trim() : null);

function parseISO(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
function diaSemana(iso: string): string {
  if (!iso) return '';
  return DIAS[parseISO(iso).getDay()] ?? '';
}

/** Uma nota fiscal como texto: "Hunter Douglas NF 1195987 · 15 vol." */
function notaTexto(n: RoteiroEvento['notas'][number]): string {
  return [
    v(n.fornecedor),
    n.numero ? `NF ${n.numero}` : null,
    n.volumes != null ? `${n.volumes} vol.` : null,
    v(n.conteudo),
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Um recurso "o que levar" como texto: "Escada (2) — grande". */
function recursoTexto(r: RoteiroEvento['recursos'][number]): string {
  const qtd = r.quantidade != null ? ` (${r.quantidade})` : '';
  const obs = v(r.observacao) ? ` — ${r.observacao}` : '';
  return `${r.nome}${qtd}${obs}`;
}

type Grupo = { destino: string | null; itens: { e: RoteiroEvento; n: number }[] };
type Bloco = {
  chave: string;
  titulo: string | null; // nome da viagem (ou null = fora de viagem)
  viagem: boolean;
  grupos: Grupo[];
};

/** Monta os blocos da folha: primeiro cada VIAGEM (na ordem de aparição), depois
 *  as paradas FORA de viagem. Dentro de cada bloco, a folha segue a ordem
 *  cronológica que veio do backend; o destino agrupa apenas paradas CONSECUTIVAS
 *  (bairro repetido mais tarde = novo trecho — a sequência montada na agenda
 *  manda). A numeração (1..N) é carimbada por último, na ordem da folha. */
function montarBlocos(eventos: RoteiroEvento[]): Bloco[] {
  // Ordem final das paradas: agrupa por viagem (1ª aparição) e joga o resto ao fim.
  const viagensOrdem: number[] = [];
  eventos.forEach((e) => {
    if (e.viagem_id != null && !viagensOrdem.includes(e.viagem_id))
      viagensOrdem.push(e.viagem_id);
  });
  const ordenados: RoteiroEvento[] = [];
  viagensOrdem.forEach((vid) =>
    ordenados.push(...eventos.filter((e) => e.viagem_id === vid)),
  );
  ordenados.push(...eventos.filter((e) => e.viagem_id == null));

  const blocos: Bloco[] = [];
  ordenados.forEach((e) => {
    const chave = e.viagem_id != null ? `v${e.viagem_id}` : 'fora';
    let b = blocos.find((x) => x.chave === chave);
    if (!b) {
      b = {
        chave,
        titulo:
          e.viagem_id != null
            ? v(e.viagem_nome) ?? `Viagem #${e.viagem_id}`
            : null,
        viagem: e.viagem_id != null,
        grupos: [],
      };
      blocos.push(b);
    }
    const destino = v(e.bairro_cidade);
    let g = b.grupos[b.grupos.length - 1];
    if (!g || g.destino !== destino) {
      g = { destino, itens: [] };
      b.grupos.push(g);
    }
    g.itens.push({ e, n: 0 });
  });
  let n = 0;
  blocos.forEach((b) =>
    b.grupos.forEach((g) => g.itens.forEach((it) => (it.n = ++n))),
  );
  return blocos;
}

function baixarBlob(blob: Blob, nome: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  URL.revokeObjectURL(url);
}

type StatusCopia = 'idle' | 'copiando' | 'ok' | 'erro';

export function RoteiroDiaPage() {
  const [params] = useSearchParams();
  const data = params.get('data') ?? '';
  const viagemId = params.get('viagem');
  const loja = params.get('loja') ?? undefined;
  const modoViagem = !!viagemId;
  const navigate = useNavigate();
  const [rotDia, setRotDia] = useState<RoteiroDia | null>(null);
  const [rotViagem, setRotViagem] = useState<RoteiroViagem | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const folhaRef = useRef<HTMLDivElement>(null);
  const [copia, setCopia] = useState<StatusCopia>('idle');

  /** Rasteriza só a folha (sem o espaço em branco do A4) e joga no clipboard como
   *  PNG. Onde o clipboard de imagem não existe, cai para baixar o arquivo. */
  async function copiarImagem() {
    const folha = folhaRef.current;
    if (!folha) return;
    setCopia('copiando');
    const minhSalvo = folha.style.minHeight;
    folha.style.minHeight = '0'; // captura só o conteúdo, não a folha A4 inteira
    try {
      const canvas = await html2canvas(folha, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
      });
      const blob = await new Promise<Blob | null>((res) =>
        canvas.toBlob(res, 'image/png'),
      );
      if (!blob) throw new Error('sem imagem');
      const podeCopiar =
        typeof ClipboardItem !== 'undefined' && !!navigator.clipboard?.write;
      if (podeCopiar) {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob }),
        ]);
      } else {
        baixarBlob(blob, `roteiro-${data || `viagem-${viagemId}`}.png`);
      }
      setCopia('ok');
    } catch {
      setCopia('erro');
    } finally {
      folha.style.minHeight = minhSalvo;
      setTimeout(() => setCopia('idle'), 2500);
    }
  }

  useEffect(() => {
    if (modoViagem) {
      api
        .get<RoteiroViagem>(`/agenda/roteiro-viagem?viagem=${viagemId}`)
        .then(setRotViagem)
        .catch((e) =>
          setErro(e instanceof ApiError ? e.message : 'Erro ao carregar o roteiro.'),
        );
      return;
    }
    if (!data) {
      setErro('Informe a data do roteiro.');
      return;
    }
    const q = new URLSearchParams({ data });
    if (loja) q.set('loja', loja);
    api
      .get<RoteiroDia>(`/agenda/roteiro?${q.toString()}`)
      .then(setRotDia)
      .catch((e) =>
        setErro(e instanceof ApiError ? e.message : 'Erro ao carregar o roteiro.'),
      );
  }, [data, viagemId, loja, modoViagem]);

  if (erro) return <div className="p-4 text-danger">{erro}</div>;
  const carregando = modoViagem ? !rotViagem : !rotDia;
  if (carregando) {
    return (
      <div className="p-4 text-secondary">
        <span className="spinner-border spinner-border-sm me-2" /> Carregando…
      </div>
    );
  }

  return (
    <div className="doc-os-wrap">
      <style>{cssDoc()}</style>

      {/* Barra de ações (não sai na impressão) */}
      <div className="no-print doc-toolbar">
        <button className="btn btn-link" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left me-1"></i> Voltar à agenda
        </button>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary"
            onClick={copiarImagem}
            disabled={copia === 'copiando'}
            title="Copiar a folha como imagem (colar no WhatsApp, e-mail…)"
          >
            <i
              className={`bi me-1 ${
                copia === 'ok'
                  ? 'bi-check-lg'
                  : copia === 'erro'
                    ? 'bi-exclamation-triangle'
                    : 'bi-clipboard'
              }`}
            ></i>
            {copia === 'ok'
              ? 'Copiado!'
              : copia === 'erro'
                ? 'Falhou'
                : copia === 'copiando'
                  ? 'Copiando…'
                  : 'Copiar imagem'}
          </button>
          <button className="btn btn-theme" onClick={() => window.print()}>
            <i className="bi bi-printer me-1"></i> Imprimir / Salvar PDF
          </button>
        </div>
      </div>

      <div className="folha" ref={folhaRef}>
        {modoViagem ? (
          <ConteudoViagem rot={rotViagem!} />
        ) : (
          <ConteudoDia rot={rotDia!} />
        )}
      </div>
    </div>
  );
}

function CabecalhoFolha({
  titulo,
  codigo,
  sub,
}: {
  titulo: string;
  codigo: string;
  sub: string;
}) {
  return (
    <div className="doc-head">
      <div className="d-flex align-items-center gap-2">
        <img
          src="/assets/img/casaa-logo-bege.png"
          alt="Casa A"
          className="doc-logo"
        />
        <div>
          <div className="doc-marca">Casa A</div>
          <div className="doc-sub">Cortinas e persianas · Búzios / Trancoso</div>
        </div>
      </div>
      <div className="text-end">
        <div className="doc-titulo">{titulo}</div>
        <div className="doc-codigo">{codigo}</div>
        <div className="doc-sub">{sub}</div>
      </div>
    </div>
  );
}

/** Estado vazio da folha: nada a imprimir. Some na impressão (ninguém imprime
 *  uma folha vazia); serve pra dar um recado claro na tela em vez do branco. */
function RoteiroVazio({ tipo }: { tipo: 'dia' | 'viagem' }) {
  return (
    <div className="rot-vazio no-print">
      <i className="bi bi-calendar-x"></i>
      <p className="rot-vazio-titulo">
        {tipo === 'viagem'
          ? 'Nenhuma parada marcada nesta viagem'
          : 'Nenhuma parada agendada neste dia'}
      </p>
      <p className="rot-vazio-sub">
        {tipo === 'viagem'
          ? 'Marque os eventos que fazem parte da viagem pelo botão de viagem no card da agenda — eles aparecem aqui.'
          : 'Agende visitas ou lembretes na agenda para este dia e o roteiro aparece aqui.'}
      </p>
    </div>
  );
}

/** Corpo de um dia: os blocos viagem/fora (comBlocos) ou, dentro de uma viagem
 *  inteira, só os grupos por destino (comBlocos=false — o dia todo já é a viagem). */
function CorpoDia({
  eventos,
  comBlocos,
}: {
  eventos: RoteiroEvento[];
  comBlocos: boolean;
}) {
  if (eventos.length === 0) return <RoteiroVazio tipo="dia" />;
  const blocos = montarBlocos(eventos);
  const temViagem = comBlocos && blocos.some((b) => b.viagem);
  return (
    <>
      {blocos.map((bloco) => (
        <section className="rot-bloco" key={bloco.chave}>
          {comBlocos && bloco.viagem && (
            <div className="rot-bloco-head">
              <i className="bi bi-truck"></i>
              Viagem · {bloco.titulo}
            </div>
          )}
          {comBlocos && !bloco.viagem && temViagem && (
            <div className="rot-bloco-head rot-bloco-fora">
              <i className="bi bi-shop"></i>
              Fora de viagem
            </div>
          )}
          {bloco.grupos.map((g, gi) => (
            <div className="rot-grupo" key={gi}>
              <h2
                className={`doc-secao${g.destino ? '' : ' doc-secao-vazia'}`}
              >
                {g.destino ?? 'Sem endereço informado'}
              </h2>
              {g.itens.map(({ e, n }) => (
                <Parada key={e.id} e={e} n={n} />
              ))}
            </div>
          ))}
        </section>
      ))}
    </>
  );
}

function ConteudoDia({ rot }: { rot: RoteiroDia }) {
  const lojaNome = rot.loja_codigo
    ? LOJAS[rot.loja_codigo] ?? rot.loja_codigo
    : null;
  return (
    <>
      <CabecalhoFolha
        titulo="Roteiro do dia"
        codigo={formatarData(rot.data)}
        sub={`${diaSemana(rot.data)}${lojaNome ? ` · ${lojaNome}` : ''}`}
      />
      <CorpoDia eventos={rot.eventos} comBlocos />
      <div className="folha-num">
        Casa A · Roteiro {formatarData(rot.data)}
        {lojaNome ? ` · ${lojaNome}` : ''}
      </div>
    </>
  );
}

function ConteudoViagem({ rot }: { rot: RoteiroViagem }) {
  const nome = v(rot.viagem_nome) ?? `Viagem #${rot.viagem_id}`;
  const datas = rot.dias.map((d) => d.data).filter(Boolean);
  const periodo =
    datas.length === 0
      ? 'Sem paradas na agenda'
      : datas.length === 1
        ? `${formatarData(datas[0])} · ${diaSemana(datas[0])}`
        : `${formatarData(datas[0])} – ${formatarData(datas[datas.length - 1])}`;
  return (
    <>
      <CabecalhoFolha titulo="Roteiro da viagem" codigo={nome} sub={periodo} />
      {rot.dias.length === 0 && <RoteiroVazio tipo="viagem" />}
      {rot.dias.map((dia) => (
        <section className="rot-dia" key={dia.data}>
          <div className="rot-dia-head">
            <i className="bi bi-calendar-event"></i>
            {formatarData(dia.data)} · {diaSemana(dia.data)}
          </div>
          <CorpoDia eventos={dia.eventos} comBlocos={false} />
        </section>
      ))}
      <div className="folha-num">Casa A · Roteiro · {nome}</div>
    </>
  );
}

function Parada({ e, n }: { e: RoteiroEvento; n: number }) {
  const nome = v(e.cliente_nome) ?? v(e.titulo) ?? v(e.os_ref) ?? 'Parada';
  const meta = [
    v(e.acao),
    v(e.pedido) ? `Ped. ${e.pedido}` : null,
    v(e.os_codigo) ? `OS ${e.os_codigo}` : null,
    v(e.hora),
  ].filter(Boolean);
  // O título do grupo já é o destino (bairro/cidade). Só vale imprimir o
  // endereço quando ele diz mais que isso — ou seja, quando tem rua/número.
  const resumo = v(e.endereco_resumo);
  const endereco = resumo && resumo !== v(e.bairro_cidade) ? resumo : null;

  return (
    <div className="rot-parada">
      <div className="rot-linha1">
        <span className="rot-num">{n}</span>
        <span className="rot-cliente">{nome}</span>
        {meta.map((m, i) => (
          <span className="rot-tag" key={i}>
            {m}
          </span>
        ))}
      </div>
      {endereco && (
        <div className="rot-sub">
          <span className="rot-rot">Endereço</span>
          <span className="rot-item rot-endereco">{endereco}</span>
          {v(e.complemento) && <span className="rot-item">{e.complemento}</span>}
        </div>
      )}
      {e.notas.length > 0 && (
        <div className="rot-sub">
          <span className="rot-rot">NF</span>
          {e.notas.map((nf, i) => (
            <span key={i} className="rot-item">
              {notaTexto(nf)}
            </span>
          ))}
        </div>
      )}
      {e.recursos.length > 0 && (
        <div className="rot-sub">
          <span className="rot-rot">Levar</span>
          {e.recursos.map((r, i) => (
            <span key={i} className="rot-item">
              {recursoTexto(r)}
            </span>
          ))}
        </div>
      )}
      {v(e.pauta) && <div className="rot-obs">{e.pauta}</div>}
    </div>
  );
}

function cssDoc() {
  return `
.doc-os-wrap { background: #e9e6df; min-height: 100vh; padding-bottom: 24px; }
.doc-toolbar {
  position: sticky; top: 0; z-index: 5; display: flex; align-items: center;
  justify-content: space-between; gap: 12px; padding: 12px 16px;
  background: #fff; border-bottom: 1px solid #e5e7eb; margin-bottom: 24px;
}
.folha {
  background: #fff; color: #1f2937; width: 794px; min-height: 1080px;
  margin: 0 auto 24px; padding: 32px 40px; box-shadow: 0 2px 12px rgba(0,0,0,.12);
  font-size: 13px; line-height: 1.45; display: flex; flex-direction: column;
}
.folha-num { margin-top: auto; padding-top: 10px; text-align: right; color: #9ca3af; font-size: 11px; }
.doc-head {
  display: flex; justify-content: space-between; align-items: flex-start;
  border-bottom: 3px solid ${MARROM}; padding-bottom: 12px; margin-bottom: 16px;
}
.doc-logo { width: 54px; height: 54px; border-radius: 8px; }
.doc-marca { font-size: 22px; font-weight: 800; color: ${MARROM}; }
.doc-sub { font-size: 11px; color: #6b7280; }
.doc-titulo { font-size: 15px; font-weight: 700; color: ${MARROM}; }
.doc-codigo { font-size: 20px; font-weight: 800; color: #111827; }
.doc-secao {
  font-size: 11px; text-transform: uppercase; letter-spacing: .6px;
  color: ${MARROM}; font-weight: 700; border-bottom: 1px solid #e5e7eb;
  padding-bottom: 4px; margin: 0 0 8px;
}
.doc-secao-vazia { color: #9ca3af; font-style: italic; }

.rot-dia { margin-bottom: 22px; }
.rot-dia-head {
  display: flex; align-items: center; gap: 8px;
  font-size: 15px; font-weight: 800; color: #111827;
  border-bottom: 2px solid ${MARROM}; padding-bottom: 6px; margin-bottom: 12px;
}
.rot-bloco { margin-bottom: 18px; }
.rot-bloco-head {
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; font-weight: 700; color: ${MARROM};
  background: #f6f3ee; border-left: 4px solid ${MARROM};
  padding: 6px 10px; border-radius: 4px; margin-bottom: 10px;
}
.rot-bloco-fora { color: #6b7280; border-left-color: #cbd5e1; background: #f7f7f6; }
.rot-grupo { margin-bottom: 12px; }
.rot-grupo:last-child { margin-bottom: 0; }
.rot-parada { padding: 8px 0; border-top: 1px dashed #e5e7eb; }
.rot-parada:first-of-type { border-top: 0; }
.rot-linha1 { display: flex; align-items: baseline; flex-wrap: wrap; gap: 8px; }
.rot-num {
  flex: 0 0 auto; width: 22px; height: 22px; border-radius: 50%;
  background: ${MARROM}; color: #fff; font-size: 12px; font-weight: 700;
  display: inline-flex; align-items: center; justify-content: center;
}
.rot-cliente { font-size: 14px; font-weight: 700; color: #111827; }
.rot-tag {
  font-size: 11px; color: #374151; background: #f3f4f6; border-radius: 4px;
  padding: 1px 7px;
}
.rot-sub { display: flex; align-items: baseline; flex-wrap: wrap; gap: 6px; margin: 3px 0 0 30px; }
.rot-rot { font-size: 10px; text-transform: uppercase; letter-spacing: .5px; color: #9ca3af; font-weight: 700; min-width: 52px; }
.rot-item { font-size: 12px; color: #374151; }
.rot-endereco { font-weight: 600; color: #1f2937; }
.rot-item + .rot-item::before { content: "•"; color: #d1d5db; margin-right: 6px; }
.rot-obs { margin: 3px 0 0 30px; font-size: 12px; font-style: italic; color: #4b5563; }

.rot-vazio { text-align: center; padding: 48px 16px; color: #6b7280; }
.rot-vazio .bi { font-size: 40px; color: #cbd5e1; display: block; margin-bottom: 12px; }
.rot-vazio-titulo { font-size: 15px; font-weight: 700; color: #374151; margin: 0 0 4px; }
.rot-vazio-sub { font-size: 12px; color: #9ca3af; max-width: 380px; margin: 0 auto; }

@media print {
  .no-print { display: none !important; }
  .doc-os-wrap { background: #fff; padding: 0; }
  .folha {
    width: auto; min-height: auto; box-shadow: none; margin: 0; padding: 0;
  }
  .rot-parada, .rot-grupo { page-break-inside: avoid; }
  @page { size: A4; margin: 14mm; }
}
`;
}
